<template>
    <div class="container content-page-wrapper projects-edit pb-5" :class="{'loading': loading}" v-if="Object.keys(planningFields).length !== 0">
        <div class="row pb-3" v-if="!editPlanning">
            <div class="col-12 m-0 p-0">
                <!-- Planning Details -->
                <h1 class="pt-5 mb-0"><product-configurator-title :product_configurator_id="planningFields.product_configurator_id" />: {{ planningFields.title }}</h1>
                <p class="lead mb-0">({{ $t('No.') }} {{ planningFields.custom_planning_id }})<project-invalid v-if="$planning_needs_update(planningFields)" separator="before" /></p>
                <router-link :to="{ name: 'projects.show', params: { id: this.planningFields.project_id } }" tag="button" class="btn btn-sm btn-light" v-if="planningFields.project_id"><i class="fas fa-chevron-left"></i> {{ $t('Back to project') }}</router-link>
            </div>
        </div>
        <div class="row" v-if="!editPlanning">
            <div class="col-12 m-0 p-0">
                <div class="dropdown d-inline-block">
                    <button class="btn btn-light btn-sm dropdown-toggle" type="button" id="project-export-button" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                        <i class="fas fa-cloud-download-alt"></i> Export
                    </button>
                    <div class="dropdown-menu" aria-labelledby="project-edit-button">
                        <download-pdf-button
                            :url="'/pdf/plannings/' + this.$route.params.id"
                            :button_class="'dropdown-item'" />
                        <download-excel-button
                            :disabled="$planning_needs_update(planningFields)"
                            :url="'/excel/plannings/' + this.$route.params.id"
                            :button_class="'dropdown-item'" />
                        <download-excel-button v-if="$can('download_mounting_rail_labels_data') && planningFields.product_configurator_id === 1"
                            :button_text="$t('Export labels')"
                            :price_option="false"
                            :production_time_option="false"
                            :url="'/excel/plannings/' + this.$route.params.id + '/labels'"
                            :button_class="'dropdown-item'" />
                        <download-dialux-data-button
                            v-if="planningFields.total_lumination_line_length && planningFields.total_lumination_line_length > 0"
                            :plannings="[planningFields]"
                            :button_class="'dropdown-item'"
                            @update-loading="updateLoading" />
                        <download-tender-text-button
                            :product_configurator_id="product_configurator_id"
                            :planning_data="planningFields"
                            :planning_id="planningFields.id"
                            :button_class="'dropdown-item'"
                            @update-loading="updateLoading" />
                    </div>
                </div>
                <button class="btn btn-sm btn-light" href="#" @click.prevent="activateEditPlanning" v-if="$can('edit_plannings') && planningFields.user_id === user.id">
                    <i class="far fa-edit"></i> {{ $t('Configure basic data') }}
                </button>
                <router-link class="btn btn-sm btn-light" tag="button" :to="{ name: 'plannings.edit', params: { id: planningFields.id } }"
                             v-if="planningFields.id"
                             :disabled="$planning_needs_update(planningFields) || planningFields.project.project_quote || planningFields.project.user_id !== user.id">
                    {{ !planningFields.total_lumination_line_length ? $t('Continue configuration') : $t('Edit configuration') }}
                </router-link >
                <button class="btn btn-sm btn-light" @click.prevent="duplicatePlanningMultiple(planningFields.id)"
                        :disabled="$planning_needs_update(planningFields) || planningDuplicating || planningFields.project.project_quote || planningFields.project.user_id !== user.id">
                    <i class="far fa-clone" :title="$t('Duplicate')"></i> <span>{{ $t('Duplicate') }}</span>
                </button>
            </div>
        </div>
        <div class="row pt-5" v-if="editPlanning && $can('edit_plannings')">
            <div class="col-12 m-0 p-0">
                <ValidationObserver v-slot="{ handleSubmit }" >
                    <form @submit.prevent="handleSubmit(updatePlanning)" class="update-planning-form">
                        <ValidationProvider
                            :name="$t('Name of lighting line')"
                            rules="required"
                            tag="div"
                            class="form-group"
                            v-slot="{ errors }">

                            <label class="lead text-white" for="planning-title">{{ $t('Name of lighting line') }}</label>
                            <input v-model="planningFields.title" type="text" id="planning-title" name="title" class="form-control">
                            <span class="text-danger">{{ errors[0] }}</span>
                            <div class="alert alert-danger" v-if="planningFieldsErrors && planningFieldsErrors.title">
                                {{ planningFieldsErrors.title[0] }}
                            </div>
                        </ValidationProvider>

                        <div class="form-group">
                            <label class="lead text-white" for="planning-description">{{ $t('Your comments and special requests') }}</label>
                            <textarea v-model="planningFields.description" id="planning-description" name="description" class="form-control"></textarea>
                            <div class="alert alert-danger" v-if="planningFieldsErrors && planningFieldsErrors.title">
                                {{ planningFieldsErrors.title[0] }}
                            </div>
                        </div>

                        <button type="submit" class="btn btn-primary" :disabled="planningFieldsSubmitting">{{ planningFieldsSubmitting ? $t('Save') + '...' : $t('Save') }}</button>
                        <button class="btn text-light" :disabled="planningFieldsSubmitting" @click.prevent="deactivateEditPlanning">{{ $t('Cancel') }}</button>
                    </form>
                </ValidationObserver>
            </div>
        </div>
        <div class="row pt-5" v-if="planningFields.total_lumination_line_length > 0">
            <div class="col-6 pl-0">
                <table class="table table-light rounded w-100">
                    <thead class="thead-dark">
                        <tr>
                            <th colspan="2" class="lead">{{ $t('Summary') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td class="font-bold">{{ $t('Total length') }}</td>
                            <td>{{ planningFields.total_lumination_line_length / 100 }} m</td>
                        </tr>
                        <tr v-if="$can('view_production_time')">
                            <td class="font-bold">{{ $t('Production time') }}</td>
                            <td>{{ planningFields.total_production_time_of_lumination_lines.toFixed(2) }} {{ $t('hours') }}</td>
                        </tr>
                        <tr v-if="$can('view_prices')">
                            <td class="font-bold">{{ $t('List price') }}</td>
                            <td>€ {{ planningFields.total_price_of_lumination_lines.toFixed(2) }}</td>
                        </tr>
                        <tr v-if="$can('view_prices') && planningFields.reduced_total_price_of_lumination_lines && planningFields.reduced_total_price_of_lumination_lines !== planningFields.total_price_of_lumination_lines">
                            <td class="font-bold"><strong>{{ $t('Reduced total price') }}</strong></td>
                            <td><strong>€ {{ planningFields.reduced_total_price_of_lumination_lines.toFixed(2) }}</strong></td>
                        </tr>
                        <tr v-if="$can('view_purchase_prices') && totalPurchasePrices">
                            <td class="font-bold"><strong>{{ $t('Total Purchase Price') }}</strong></td>
                            <td><strong>€ {{ totalPurchasePrices }}</strong></td>
                        </tr>
                    </tbody>
                </table>

            </div>
            <div class="col-6 pr-0">
                <h2 class="lead py-3" v-if="planningFields.description">{{ $t('Customer comments') }}</h2>
                <p class="text-light" v-if="planningFields.description">{{ planningFields.description }}</p>
            </div>
        </div>
        <div class="row mt-3">
            <div class="col-12 m-0 p-0" v-if="luminaireLineDataSet && luminaireLineData && luminaireLineData.length > 0">
                <h2 class="mt-5 mb-3">{{ $t('Configuration') }}</h2>
                <table class="table table-light rounded">
                    <thead class="thead-dark">
                        <tr>
                            <th class="lead">{{ $t('Article') }}</th>
                            <th class="lead">{{ $t('Article no.') }}</th>
                            <th class="lead">{{ $t('Pcs.') }}</th>
                            <th class="lead" v-if="$can('view_prices')">{{ $t('List price/piece') }}</th>
                            <th class="lead" v-if="$can('view_prices')">{{ $t('Total list price') }}</th>
                            <th class="lead" v-if="$can('view_production_time')">{{ $t('Production time') }}</th>
                            <th class="lead" v-if="$can('view_purchase_prices')">{{ $t('Purchase price/piece') }}</th>
                            <th class="lead" v-if="$can('view_purchase_prices')">{{ $t('Total Purchase Price') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="planningFields.number_of_led_modules > 0 && planningFields.product_configurator_id === 1">
                            <td>{{ $t(planningFields.led_module_name) }}</td>
                            <td>{{ planningFields.sku_of_led_module }}</td>
                            <td>{{ planningFields.number_of_led_modules }}</td>
                            <td v-if="$can('view_prices')">€ {{ planningFields.price_of_led_module }}</td>
                            <td v-if="$can('view_prices')">€ {{ planningFields.total_price_of_led_modules.toFixed(2) }}</td>
                            <td v-if="$can('view_production_time')">{{ (0.17 * planningFields.number_of_led_modules).toFixed(2) }}</td>
                            <td v-if="$can('view_purchase_prices') && planningFields.purchase_price_of_led_module">€ {{ planningFields.purchase_price_of_led_module.toFixed(2) }}</td>
                            <td v-if="$can('view_purchase_prices') && planningFields.total_purchase_price_of_led_modules">€ {{ planningFields.total_purchase_price_of_led_modules.toFixed(2) }}</td>
                        </tr>
                        <tr v-for="product in luminaireLineData" v-if="product && product.quantity != 0">
                            <td>{{ $t(product.title) }}</td>
                            <td>{{ product.sku }}</td>
                            <td>{{ product.quantity }}</td>
                            <td v-if="$can('view_prices')">€ {{ product.price.toFixed(2) }}</td>
                            <td v-if="$can('view_prices')">€ {{ product.price_total.toFixed(2) }}</td>
                            <td v-if="$can('view_production_time')">{{ product.production_time_total }}</td>
                            <td v-if="$can('view_purchase_prices') && product.purchase_price">€ {{ product.purchase_price.toFixed(2) }}</td>
                            <td v-if="$can('view_purchase_prices') && product.purchase_price_raw">€ {{ (product.purchase_price_raw * product.quantity / 100).toFixed(2) }}</td>
                        </tr>
<!--                        Add Special Products here -->
                        <tr v-for="specialProduct in special_products" v-if="specialProduct && specialProduct.quantity != 0">
                            <td>{{ $t('Special product') }}: {{ specialProduct.sku }}</td>
                            <td>{{ specialProduct.sku }}</td>
                            <td>{{ specialProduct.quantity }}</td>
                            <td v-if="$can('view_prices')">€ {{  specialProduct.price.toFixed(2) }}</td>
                            <td v-if="$can('view_prices')">€ {{  (specialProduct.price*specialProduct.quantity).toFixed(2) }}</td>
                            <td v-if="$can('view_production_time')">0.00</td>
                            <td v-if="$can('view_purchase_prices')">€ {{  specialProduct.price.toFixed(2) }}</td>
                            <td v-if="$can('view_purchase_prices')">€ {{  (specialProduct.price*specialProduct.quantity).toFixed(2) }}</td>
                        </tr>
                    </tbody>
                    <tfoot class="thead-dark">
                        <tr v-if="$can('view_prices') || $can('view_production_time')">
                            <th></th>
                            <th></th>
                            <th></th>
                            <th>{{ $t('TOTAL') }}:</th>
                            <th v-if="$can('view_prices')">€ {{ planningFields.total_price_of_lumination_lines.toFixed(2) }}</th>
                            <th v-if="$can('view_production_time')">{{ planningFields.total_production_time_of_lumination_lines.toFixed(2) }} h</th>
                            <th v-if="$can('view_purchase_prices')">Total</th>
                            <th v-if="$can('view_purchase_prices') && totalPurchasePrices">€ {{ totalPurchasePrices }}</th>
                        </tr>
                    </tfoot>
                </table>

                <div v-if="planningFields.total_lumination_line_length > 0 && mountingRailArrangement">
                    <h2 class="mt-5 mb-3">{{ $t('Arrangement') }}</h2>
                    <router-link class="btn btn-sm btn-primary text-white mb-3" tag="button" :to="{ name: 'plannings.arrange', params: { id: planningFields.id } }"
                                 v-if="planningFields.id && (!planningFields.project.project_quote && !planningFields.project.project_ordered && !planningFields.project.project_finished) && planningFields.total_lumination_line_length && planningFields.total_lumination_line_length > 0"
                                 :disabled="$planning_needs_update(planningFields)">

                            <span v-if="product_configurator_id === 1">{{ $t('Edit arrangement & PIN configuration') }}</span><span v-else>{{ $t('Edit arrangement') }}</span>
                    </router-link>

                    <mounting-rails-with-modules-table v-if="product_configurator_id === 1 && planningFields.total_lumination_line_length > 0"
                        :mountingRailArrangementData="mountingRailArrangementData"
                        :emergency_light="emergency_light"
                        :product_configurator_id="product_configurator_id"
                        :all-api-calls-loaded="allApiCallsLoaded" />
                    <mounting-rails-table v-else-if="product_configurator_id === 3 && planningFields.total_lumination_line_length > 0"
                        :mountingRailArrangementData="mountingRailArrangementData"
                        :emergency_light="emergency_light"
                        :product_configurator_id="product_configurator_id"
                        :all-api-calls-loaded="allApiCallsLoaded" />
                </div>
                <div class="alert alert-warning" role="alert" v-else-if="planningFields.product_configurator_id === 1">
                    {{ $t('Not configured yet') }}
                </div>
            </div>
            <div class="col-12 m-0 p-0" v-else>
                <div class="alert alert-warning" role="alert">
                    {{ $t('Not configured yet') }}
            </div>
        </div>
    </div>
        <div class="row mt-3">
            <div class="col-12 m-0 p-0">
            <button class="btn btn-outline-danger" @click.prevent="deletePlanning(planningFields.id)" :disabled="planningDeleting || planningFields.project.project_quote || planningFields.project.user_id !== user.id"><i class="far fa-trash-alt" :title="$t('Delete')"></i> {{ $t('Delete') }}</button>
        </div>
        </div>
    </div>

    <div class="container content-page-wrapper" v-else :class="{'loading': loading}">
        <div class="row py-5">
            <div class="col-12">
                <display-error-messages
                    :error="loadingError"
                    title="Lichtband" />
            </div>
        </div>
    </div>
</template>

<script>


import {mapGetters} from "vuex";
import MountingRailsTable from "./MountingRailsTable.vue";
import MountingRailsWithModulesTable from "./MountingRailsWithModulesTable.vue";
import ProductConfiguratorTitle from "../Utils/Titles/ProductConfiguratorTitle.vue";
import ProjectInvalid from "../Utils/Errors/ProjectInvalid.vue";

export default {
    components: {ProjectInvalid, ProductConfiguratorTitle, MountingRailsWithModulesTable, MountingRailsTable},

    data() {
        return {
            loading: false,
            product_configurator_id: null,

            planningFields: {},
            loadingError: null,

            // Update Planning
            planningFieldsErrors: {},
            planningFieldsSubmitting: false,
            editPlanning: false,

            // Luminaire Line
            luminaireLineProducts: [],
            luminaireLineData: {},
            luminaireLineDataStructure: [
                {
                    id_key: 'type_of_mounting_rail_504',
                    quantity_key: 'number_of_mounting_rail_504'
                },
                {
                    id_key: 'type_of_mounting_rail_336',
                    quantity_key: 'number_of_mounting_rail_336'
                },
                {
                    id_key: 'type_of_mounting_rail_168',
                    quantity_key: 'number_of_mounting_rail_168'
                },
                {
                    id_key: 'type_of_mounting_rail_504_0',
                    quantity_key: 'number_of_mounting_rail_504_0'
                },
                {
                    id_key: 'type_of_mounting_rail_504_1',
                    quantity_key: 'number_of_mounting_rail_504_1'
                },
                {
                    id_key: 'type_of_mounting_rail_504_2',
                    quantity_key: 'number_of_mounting_rail_504_2'
                },
                {
                    id_key: 'type_of_mounting_rail_504_3',
                    quantity_key: 'number_of_mounting_rail_504_3'
                },
                {
                    id_key: 'type_of_mounting_rail_336_0',
                    quantity_key: 'number_of_mounting_rail_336_0'
                },
                {
                    id_key: 'type_of_mounting_rail_336_1',
                    quantity_key: 'number_of_mounting_rail_336_1'
                },
                {
                    id_key: 'type_of_mounting_rail_336_2',
                    quantity_key: 'number_of_mounting_rail_336_2'
                },
                {
                    id_key: 'type_of_mounting_rail_168_0',
                    quantity_key: 'number_of_mounting_rail_168_0'
                },
                {
                    id_key: 'type_of_mounting_rail_168_1',
                    quantity_key: 'number_of_mounting_rail_168_1'
                },
                {
                    id_key: 'type_of_optics_504',
                    quantity_key: 'number_of_optics_504'
                },
                {
                    id_key: 'type_of_optics_336',
                    quantity_key: 'number_of_optics_336'
                },
                {
                    id_key: 'type_of_optics_168',
                    quantity_key: 'number_of_optics_168'
                },
                {
                    id_key: 'type_of_protection_class_504',
                    quantity_key: 'number_of_protection_class_504'
                },
                {
                    id_key: 'type_of_protection_class_336',
                    quantity_key: 'number_of_protection_class_336'
                },
                {
                    id_key: 'type_of_protection_class_168',
                    quantity_key: 'number_of_protection_class_168'
                },
                {
                    id_key: 'type_of_blind_modules',
                    quantity_key: 'number_of_blind_modules'
                },
                {
                    id_key: 'type_of_blind_modules_168',
                    quantity_key: 'number_of_blind_modules_168'
                },
                {
                    id_key: 'type_of_blind_modules_336',
                    quantity_key: 'number_of_blind_modules_336'
                },
                {
                    id_key: 'type_of_blind_modules_504',
                    quantity_key: 'number_of_blind_modules_504'
                },
                {
                    id_key: 'type_of_electrical_feeds_start',
                    quantity_key: 'number_of_electrical_feeds_start'
                },
                {
                    id_key: 'type_of_electrical_feeds_middle',
                    quantity_key: 'number_of_electrical_feeds_middle'
                },
                {
                    id_key: 'type_of_electrical_feeds_end',
                    quantity_key: 'number_of_electrical_feeds_end'
                },
                {
                    id_key: 'type_of_ceiling_mounts',
                    quantity_key: 'number_of_ceiling_mounts'
                },
                {
                    id_key: 'type_of_mounting_adapters',
                    quantity_key: 'number_of_mounting_adapters'
                },
                {
                    id_key: 'type_of_suspensions',
                    quantity_key: 'number_of_suspensions'
                },
                {
                    id_key: 'type_of_mount_connectors',
                    quantity_key: 'number_of_mount_connectors'
                },
                {
                    id_key: 'type_of_emergency_light',
                    quantity_key: 'number_of_emergency_lights'
                },
                {
                    id_key: 'type_of_luminaire_control',
                    quantity_key: 'number_of_luminaire_controls'
                },
                {
                    id_key: 'type_of_luminaire_control_slave',
                    quantity_key: 'number_of_luminaire_control_slaves'
                },
                {
                    id_key: 'type_of_luminaire_control_submodule',
                    quantity_key: 'number_of_luminaire_control_submodules'
                },
                {
                    id_key: 'type_of_luminaire_control_submodule_slave',
                    quantity_key: 'number_of_luminaire_control_submodule_slaves'
                },
                {
                    id_key: 'type_of_universal_adapter',
                    quantity_key: 'number_of_universal_adapters'
                },
                {
                    id_key: 'type_of_universal_adapters_sm',
                    quantity_key: 'number_of_universal_adapters_sm'
                },
                {
                    id_key: 'type_of_universal_adapters_lg',
                    quantity_key: 'number_of_universal_adapters_lg'
                },
                {
                    id_key: 'type_of_universal_connectors',
                    quantity_key: 'number_of_universal_connectors'
                },
                {
                    id_key: 'type_of_universal_connectors_v2',
                    quantity_key: 'number_of_universal_connectors_v2'
                },
                {
                    id_key: 'type_of_universal_connectors_v3',
                    quantity_key: 'number_of_universal_connectors_v3'
                },
                {
                    id_key: 'type_of_universal_connector_submodules',
                    quantity_key: 'number_of_universal_connector_submodules'
                },
                {
                    id_key: 'type_of_universal_connector_submodules_v2',
                    quantity_key: 'number_of_universal_connector_submodules_v2'
                },
                {
                    id_key: 'type_of_universal_connector_submodules_v3',
                    quantity_key: 'number_of_universal_connector_submodules_v3'
                },
                {
                    id_key: 'type_of_remote_controls',
                    quantity_key: 'number_of_remote_controls'
                },
                {
                    id_key: 'type_of_mounting_adapter_tool',
                    quantity_key: 'number_of_mounting_adapter_tools'
                },
                {
                    id_key: 'type_of_cable_route_168',
                    quantity_key: 'number_of_cable_routes_168'
                },
                {
                    id_key: 'type_of_cable_route_336',
                    quantity_key: 'number_of_cable_routes_336'
                },
                {
                    id_key: 'type_of_cable_route_504',
                    quantity_key: 'number_of_cable_routes_504'
                },
                {
                    id_key: 'type_of_connecting_cable',
                    quantity_key: 'number_of_connecting_cable'
                },
                {
                    id_key: 'type_of_connecting_cable_middle',
                    quantity_key: 'number_of_connecting_cable_middle'
                },
                {
                    id_key: 'type_of_warranties',
                    quantity_key: 'number_of_warranties'
                },
            ],
            moduleOrderData: [],
            mountingRailArrangementData: [],
            mountingRailArrangement: [],
            special_products: [],

            // States
            planningDataLoaded: false,
            productDataLoaded: false,
            luminaireLineDataSet: false,
            planningDeleting: false,
            planningDuplicating: false,
            emergency_light: {},
            totalPurchasePrices: 0,

        };


    },

    created() {
        // API Calls
        this.getPlanning();
        if (this.$can('view_purchase_prices')) {
            this.getTotalPurchasePrices();
        }
        this.getLuminaireLineProducts();
    },

    methods: {

        activateEditPlanning() {
            this.editPlanning = true;
        },
        deactivateEditPlanning() {
            this.editPlanning = false;
            this.planningDataLoaded = false;
            this.getPlanning();
        },

        getPlanning() {
            this.loading = true;
            axios.get('/api/plannings/' + this.$route.params.id + '?product_configurator_id=' + this.product_configurator_id + '&paginate=0')
                .then(response => {
                    this.planningFields = response.data.data;
                    this.product_configurator_id = this.planningFields.product_configurator_id;
                    this.mountingRailArrangementData = JSON.parse(this.planningFields.mounting_rail_arrangement);
                    this.planningDataLoaded = true;
                }).catch(error => {
                    this.loadingError = error.response;
                    this.loading = false;
            });
        },

        getTotalPurchasePrices() {
            this.loading = true;
            axios.get('/api/plannings/' + this.$route.params.id + '/total_planning_purchase_prices')
                .then(response => {
                    this.totalPurchasePrices = response.data;
                }).catch(error => {
                    this.loadingError = error.response;
                    this.loading = false;
            });
        },

        getLuminaireLineProducts() {

            axios.get('/api/luminaire_line_products?enabled=0')
                .then((response) => {
                    if (typeof response.data === 'object') {
                        this.luminaireLineProducts = Object.values(response.data);
                    } else {
                        this.luminaireLineProducts = response.data;
                    }
                    this.productDataLoaded = true;
                })
                .catch(function(error) {
                    console.log(error);
                    this.loading = false;
                });

        },

        updatePlanning() {
            this.planningFieldsSubmitting = true;
            let planningData = {
                title: this.planningFields.title,
                description: this.planningFields.description
            };
            axios.put('/api/plannings/' + this.$route.params.id + '/edit', planningData )
                .then(response => {
                    this.$swal.fire({
                        icon: 'success',
                        title: this.$t('Updated successfully'),
                        confirmButtonColor: '#3CA2A4'
                    });
                    this.planningFieldsSubmitting = false;
                    this.planningDataLoaded = false;
                    this.editPlanning = false;
                    this.getPlanning();
                }).catch(error => {
                this.$swal.fire({
                    icon: 'error',
                    title: this.$t('There was an error'),
                    text: this.$t('Please check the form'),
                    confirmButtonColor: '#3CA2A4'
                });
                if (error.response.status === 422) { // Validation Error
                    this.planningFieldsErrors = error.response.data.errors;
                }
                this.planningFieldsSubmitting = false;
            });
        },

        deletePlanning(planning_id) {
            this.$swal.fire({
                title: this.$t('Are you sure?'),
                text: this.$t('After deleting the lighting line, the data can not be restored.'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#e3342f',
                cancelButtonColor: '#393e42',
                confirmButtonText: this.$t('Yes, delete!'),
                cancelButtonText: this.$t('Cancel')
            }).then((result) => {
                if (result.value) {
                    this.planningDeleting = true;
                    this.loading = true;

                    axios.delete('/api/plannings/' + planning_id)
                        .then(response => {
                            this.$swal.fire({
                                icon: 'success',
                                title: this.$t('Deleted successfully')
                            });
                            this.planningDeleting = false;
                            this.loading = false;
                            this.$router.push({ name: 'projects.show', params: { id: this.planningFields.project.id } });

                        }).catch(error => {
                        this.$swal.fire({ icon: 'error', title: this.$t('There was an error')});
                        this.planningDeleting = false;
                        this.loading = false;
                    });

                }
            })
        },

        duplicatePlanningMultiple(planning_id) {
            this.$swal.fire({
                title: this.$t('Duplicates'),
                text: this.$t('Please enter the number of duplicates'),
                icon: 'question',
                showCancelButton: true,
                confirmButtonColor: '#3CA2A4',
                cancelButtonColor: '#393e42',
                confirmButtonText: this.$t('Duplicate'),
                cancelButtonText: this.$t('Cancel'),
                input: 'range',
                inputAttributes: {
                    min: 1,
                    max: 20,
                    step: 1
                },
                inputValue: 1,
                inputValidator: (value) => {
                    if ( !value ) {
                        return this.$t('Please enter a valid number')
                    }
                    if ( value < 1 || value > 20 ) {
                        return this.$t('Please enter a number between 1 and 20')
                    }
                }
            }).then((result) => {
                if (result.value) {
                    this.planningDuplicating = true;
                    this.loading = true;
                    let planningQuantity = { quantity: result.value };

                    axios.put('/api/plannings/' + planning_id + '/duplicate_multiple', planningQuantity)
                        .then(response => {
                            this.$swal.fire({
                                icon: 'success',
                                title: this.$t('Duplicated successfully'),
                                confirmButtonColor: '#3CA2A4'
                            });
                            this.planningDuplicating = false;
                            this.loading = false;
                        }).catch(error => {
                        this.$swal.fire({icon: 'error', title: this.$t('There was an error'), confirmButtonColor: '#3CA2A4'});
                        this.planningDuplicating = false;
                        this.loading = false;
                    });
                }
            })
        },

        setLuminaireLineProductData() {

            let luminaireLineKey = this.luminaireLineDataStructure;

            this.luminaireLineData = luminaireLineKey.map(item => {
                let price = 0;
                let price_total = 0;
                let purchase_price = 0;
                let purchase_price_raw = 0;

                if (this.planningFields[item.id_key]) {

                    const product = this.luminaireLineProducts.find( element => element.id === this.planningFields[item.id_key]);

                    if (item.id_key === 'type_of_warranties') {
                        price = this.planningFields.total_price_of_warranties ? this.planningFields.total_price_of_warranties : 0;
                        price_total = this.planningFields.total_price_of_warranties ? this.planningFields.total_price_of_warranties : 0;

                    } else {
                        price = (product.price ? product.price : 0);
                        price_total = (product.price ? product.price : 0) * this.planningFields[item.quantity_key];
                    }

                    purchase_price = product.purchase_price;
                    purchase_price_raw = product.purchase_price.toFixed(2).toString().replace('.', '');

                    return {
                        id: this.planningFields[item.id_key],
                        title: product.title,
                        sku: product.sku,
                        price: price,
                        price_total: price_total,
                        purchase_price: purchase_price,
                        purchase_price_raw: purchase_price_raw,
                        production_time: product.production_time ? product.production_time : 0,
                        quantity: this.planningFields[item.quantity_key] ? this.planningFields[item.quantity_key] : 0,
                        production_time_total: ((product.production_time ? product.production_time : 0 ) * (this.planningFields[item.quantity_key] ? this.planningFields[item.quantity_key] : 0 )).toFixed(2),
                    };
                } else {
                    return null;
                }
            }).filter(item => item !== null);

            if (this.planningFields.type_of_emergency_light && this.planningFields.number_of_emergency_lights > 0) {
                const emergencyLight = this.luminaireLineProducts.find( element => element.id === this.planningFields.type_of_emergency_light);
                this.emergency_light = {
                    id: this.planningFields.type_of_emergency_light,
                    title: emergencyLight.title,
                    sku: emergencyLight.sku,
                };
            }

            this.luminaireLineDataSet = true;
            this.loading = false;

        },

        setSpecialProductData() {
            //convert planningFormFields.special_products from json to array
            if (this.planningFields.special_products) {
                this.special_products = JSON.parse(this.planningFields.special_products);
            }
        },

        updateLoading(value) {
            this.loading = value;
        },

    },

    watch: {
        allApiCallsLoaded: {
            handler: function(loaded, oldVal) {
                if (loaded) {
                    this.setLuminaireLineProductData();
                    this.setSpecialProductData();
                }
            },
            deep: true
        }

    },

    computed: {

        ...mapGetters({
            authenticated: 'auth/authenticated',
            user: 'auth/user',
            permissions: 'auth/permissions'
        }),

        allApiCallsLoaded() {
            return this.productDataLoaded && this.planningDataLoaded;
        }
    }

};
</script>
